import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { investmentSheetCraService } from '../../services/investmentSheetService';
import { formatCurrency } from '../../utils/format';
import '../common/ConfirmModal.css';

interface TicketInfo {
  valorTicket: number;
  importe: number;
  numTicketsCalculated: number;
  exact: boolean;
  ticketsLibres: number;
}

interface AssignTicketsModalProps {
  isOpen: boolean;
  sheetId: string;
  investorName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const AssignTicketsModal: React.FC<AssignTicketsModalProps> = ({
  isOpen,
  sheetId,
  investorName,
  onSuccess,
  onCancel,
}) => {
  const { showToast } = useToast();
  const [ticketInfo, setTicketInfo] = useState<TicketInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [errorInfo, setErrorInfo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sheetId) {
      setLoadingInfo(true);
      setErrorInfo('');
      setTicketInfo(null);
      investmentSheetCraService.getTicketInfo(sheetId)
        .then((res) => setTicketInfo(res.data))
        .catch((err: any) => {
          setErrorInfo(err.response?.data?.message || 'Error al obtener info de tickets');
        })
        .finally(() => setLoadingInfo(false));
    }
  }, [isOpen, sheetId]);

  if (!isOpen) return null;

  const canAssign = ticketInfo && ticketInfo.exact && ticketInfo.numTicketsCalculated > 0
    && ticketInfo.ticketsLibres >= ticketInfo.numTicketsCalculated;

  const handleConfirm = async () => {
    if (!ticketInfo || !canAssign) return;

    setLoading(true);
    try {
      await investmentSheetCraService.assignTickets(sheetId, ticketInfo.numTicketsCalculated);
      showToast(`${ticketInfo.numTicketsCalculated} ticket(s) asignados correctamente`);
      onSuccess();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Error al asignar tickets';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderWarnings = () => {
    if (!ticketInfo) return null;
    const warnings: string[] = [];
    if (!ticketInfo.exact) {
      warnings.push('El importe no es divisible exactamente por el valor del ticket.');
    }
    if (ticketInfo.numTicketsCalculated <= 0) {
      warnings.push('El importe es inferior al valor de un ticket.');
    }
    if (ticketInfo.ticketsLibres < ticketInfo.numTicketsCalculated) {
      warnings.push(`No hay suficientes tickets libres (disponibles: ${ticketInfo.ticketsLibres}).`);
    }
    if (warnings.length === 0) return null;
    return (
      <div style={{ marginTop: 16, padding: 12, backgroundColor: 'rgba(245, 124, 0, 0.1)', border: '1px solid rgba(245, 124, 0, 0.3)', borderRadius: 6, fontSize: 14, color: '#ff9800' }}>
        {warnings.map((w, i) => <p key={i} style={{ margin: i > 0 ? '6px 0 0' : 0 }}>{w}</p>)}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header info">
          <h3>Asignar Tickets</h3>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: 16 }}>
            Inversor: <strong>{investorName}</strong>
          </p>

          {loadingInfo && <p style={{ color: 'var(--text-secondary)' }}>Cargando info de tickets...</p>}
          {errorInfo && <p style={{ color: '#f44336' }}>{errorInfo}</p>}

          {ticketInfo && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: 15 }}>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Valor del ticket</span>
                  <p style={{ margin: '4px 0 0', fontWeight: 600 }}>{formatCurrency(ticketInfo.valorTicket)}</p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Importe de la inversion</span>
                  <p style={{ margin: '4px 0 0', fontWeight: 600 }}>{formatCurrency(ticketInfo.importe)}</p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Tickets a asignar</span>
                  <p style={{ margin: '4px 0 0', fontWeight: 600, fontSize: 18 }}>{ticketInfo.numTicketsCalculated}</p>
                </div>
                <div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Tickets libres disponibles</span>
                  <p style={{ margin: '4px 0 0', fontWeight: 600 }}>{ticketInfo.ticketsLibres}</p>
                </div>
              </div>
              {renderWarnings()}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="button" className="btn-info" onClick={handleConfirm} disabled={loading || !canAssign}>
            {loading ? 'Asignando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignTicketsModal;
